<script setup lang="ts">
import { Plus } from 'lucide-vue-next'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import type { CreateItem } from '~~/src/composables/useNavigationData'
import type { ElementalTypeEnum } from '~~/src/types/elemental'
import CreateMenuContent from '@/components/CreateMenu/CreateMenuContent.vue'

interface Props {
  createItems: CreateItem[]
}

const props = defineProps<Props>()

defineOptions({
  name: 'CreateElementDrawer',
})

const isOpen = ref(false)
const currentElementType = ref<ElementalTypeEnum | 'form' | null>(null)
const isCreating = ref(false)

function closeDrawer() {
  isOpen.value = false
}

function handleCreating(elementalType: ElementalTypeEnum | 'form') {
  currentElementType.value = elementalType
  isCreating.value = true
}

function getElementLabel(elementalType: ElementalTypeEnum | 'form' | null): string {
  if (!elementalType)
    return 'Element'
  const item = props.createItems.find(i => i.elementalType === elementalType)
  return item?.title || 'Element'
}

// Reset creating state when drawer closes
watch(isOpen, (newValue) => {
  if (!newValue) {
    setTimeout(() => {
      isCreating.value = false
      currentElementType.value = null
    }, 300)
  }
})
</script>

<template>
  <div class="create-drawer-wrapper">
    <!-- Trigger Button -->
    <button
      type="button"
      class="create-trigger"
      aria-label="Create/Sell"
      @click="isOpen = true"
    >
      <Plus :size="24" />
    </button>

    <!-- Drawer -->
    <Drawer v-model:open="isOpen">
      <DrawerContent>
        <DrawerHeader class="text-center pb-2">
          <DrawerTitle>
            Create/Sell
          </DrawerTitle>
        </DrawerHeader>

        <div class="px-4 pb-8">
          <CreateMenuContent
            :create-items="createItems"
            @close="closeDrawer"
            @creating="handleCreating"
          />
        </div>
      </DrawerContent>
    </Drawer>

    <!-- Loading Overlay -->
    <Teleport v-if="isCreating" to="body">
      <div class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50">
        <LoadingOverlay :message="`Creating ${getElementLabel(currentElementType)}...`" />
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.create-drawer-wrapper {
  @apply shrink-0;
}

.create-trigger {
  @apply
    h-12
    w-12
    rounded-full
    shrink-0
    flex
    items-center
    justify-center
    text-white
    shadow-lg
    transition-all
    duration-200
    bg-primary
    hover:bg-primary/90;

  svg {
    @apply w-6 h-6;
  }
}
</style>
